import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { translate } from 'utils/index';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';

import { ReactComponent as ArrowIconRight } from 'assets/svgs/arrowRight.svg';
import { ReactComponent as ArrowIconLeft } from 'assets/svgs/arrowLeft.svg';
import { ReactComponent as TrashIcon } from 'assets/svgs/small-trash.svg';

import CategoryTabLabel from 'pages/Vendors/VendorManage/Tabs/CategoryTab/categoryTabLabel';
import { generalMessages } from 'constants/messages';
import { useVendorManageContext } from 'hooks/useVendorManageContext';
import NewVendorFooter from 'components/NewVendorFooter';

import Tabs from 'ui/Tabs';
import TabAddButton from 'ui/Tabs/tab-add-button';
import Loader from 'ui/Loader';
import ScoreChip from 'ui/ScoreChip';

import { messages } from './messages';
import CategoryForm from './categoryForm';

import './index.scss';
import RemoveConfirmationModal from 'components/RemoveConfirmationModal';

const CategoryTab = ({ handleTabChange, handleBackTab, prevAction }) => {
  const {
    currentVendor,
    vendorAssessmentsData,
    updateVendor,
    onChangeTitle,
    onDeleteCategory,
    isAddingAnalysis,
    isRemovingAnalysis,
  } = useVendorManageContext();
  const { isLoading } = useSelector((state) => state.vendors);
  const { vendorId } = useParams();
  const [tabs, setTabs] = useState(vendorAssessmentsData);
  const [isModalOpen, setModalOpen] = useState(false);

  const [activeTab, setActiveTab] = useState(tabs[0]?.id || []);

  useEffect(() => {
    // setting analysis tabs to vendor's assessments array
    setTabs(vendorAssessmentsData);
  }, [vendorAssessmentsData]);

  // mapping assessment id to categories controls map
  const mapControls = useMemo(() => {
    const result = new Map();
    if (tabs.length === 0) {
      return result;
    }
    tabs.forEach((assessment) => {
      result.set(
        assessment.id,
        assessment.categories
          .flatMap((category) => category.controls)
          ?.map((categoryControl) => ({
            ...categoryControl,
            answerData: assessment.answer_types[categoryControl.answer_type_idx],
          }))
          .reduce((acc, categoryControl) => {
            const { categoryId } = categoryControl;
            const control = acc.get(categoryId);
            if (control) {
              control.push(categoryControl);
              acc.set(categoryId, control);
            } else {
              acc.set(categoryId, [categoryControl]);
            }
            return acc;
          }, new Map()),
      );
      return {};
    });

    return result;
  }, [tabs]);

  const categories = useMemo(() => {
    const result = [];
    if (tabs.length === 0) {
      return result;
    }
    tabs.forEach((assessment) => {
      result.push(
        assessment?.categories?.map((category) => {
          const control = mapControls.get(assessment.id);
          const mappedControl = control.get(category.id);
          if (mappedControl) {
            return {
              ...category,
              controls: mappedControl,
            };
          }

          return category;
        }),
      );
      return {};
    });

    return result;
  }, [mapControls, tabs]);

  // handle next button in footer
  const onSubmit = (event) => {
    event.preventDefault();
    currentVendor ? updateVendor() : handleTabChange();
  };

  // handle analysis tab change
  const addTab = async () => {
    const newId = await updateVendor(true);
    setActiveTab(newId);
  };

  const toggleRemoveModal = () => {
    setModalOpen((state) => !state);
  };

  const handleOkModal = async () => {
    toggleRemoveModal();
    const index = tabs.findIndex((tab) => tab.id === activeTab);
    await onDeleteCategory(activeTab);
    if (index === 0) {
      return setActiveTab(tabs[1].id);
    }
    return setActiveTab(tabs[index - 1].id);
  };

  const tabPanes = useMemo(() => {
    return tabs?.map((assessment, index) => {
      return (
        <Tabs.Pane
          tab={
            <CategoryTabLabel
              id={assessment.id}
              activeTab={activeTab}
              saveAction={(value) => {
                onChangeTitle(value, assessment.id);
              }}
              text={assessment.title || `${messages.tabLabel}`}
            />
          }
          key={assessment.id}
          data-test={`vendor-category-tab-tabs-pane-${assessment.title || messages.tabLabel}`}
        >
          {assessment.total_score.score > 0 && (
            <div className="category-tab__inherent-risk">
              <span className="category-tab__inherent-risk-label">
                {translate(messages.inherentRisk)}:
              </span>
              <ScoreChip value={assessment.total_score.score} />
            </div>
          )}
          {categories[index]?.map((category) => {
            return (
              <CategoryForm key={category.id} assessmentId={assessment.id} category={category} />
            );
          })}
        </Tabs.Pane>
      );
    });
  }, [tabs, activeTab]);

  const contentForEditing = (
    <Tabs
      defaultActiveKey={activeTab}
      activeKey={activeTab}
      tabBarExtraContent={{
        right: (
          <TabAddButton
            loading={isLoading}
            onClick={addTab}
            text={translate(messages.tabAddButtonText)}
          />
        ),
      }}
      inner
      onChange={(tab) => {
        setActiveTab(tab);
      }}
    >
      {tabPanes}
    </Tabs>
  );

  const contentForAdding = (
    <>
      {tabs[0]?.total_score.score > 0 && (
        <div className="category-tab__inherent-risk">
          {translate(messages.inherentRisk)}: <ScoreChip value={tabs[0]?.total_score.score} />
        </div>
      )}
      {categories[0]?.map((category, index) => {
        return (
          <CategoryForm
            key={`footer-button_${index}`}
            assessmentId={tabs[0]?.id}
            category={category}
          />
        );
      })}
    </>
  );

  return (
    <>
      {isLoading && <Loader />}
      <div className="category-tab">
        {tabs.length > 0 && vendorId && (
          <div className="category-tab__delete-tab" onClick={toggleRemoveModal}>
            <TrashIcon /> {translate(messages.deleteButton)}
          </div>
        )}
        {vendorId ? contentForEditing : contentForAdding}
      </div>
      {vendorId ? null : (
        <NewVendorFooter
          prevBeforeComponent={!currentVendor && <ArrowIconLeft width={13} height={14} />}
          nextAfterComponent={!currentVendor && <ArrowIconRight width={13} height={14} />}
          nextAction={onSubmit}
          prevAction={currentVendor ? prevAction : handleBackTab}
          nextLabel={
            currentVendor ? translate(messages.saveAndClose) : translate(generalMessages.next)
          }
          prevLabel={
            currentVendor ? translate(generalMessages.cancel) : translate(generalMessages.back)
          }
          isNextLoading={isLoading && !isAddingAnalysis && !isRemovingAnalysis}
          isNextDisabled={isRemovingAnalysis || isAddingAnalysis}
          dataTestNext="vendor-category-tab-button-footer-next"
          dataTestPrev="vendor-category-tab-button-footer-back"
        />
      )}
      <RemoveConfirmationModal
        onCancel={toggleRemoveModal}
        onOk={handleOkModal}
        open={isModalOpen}
        text={translate(messages.removeAnalysisText)}
        headerText={translate(messages.removeAnalysisTextHeader)}
      />
    </>
  );
};

CategoryTab.propTypes = {
  handleTabChange: PropTypes.func,
  handleBackTab: PropTypes.func,
  prevAction: PropTypes.func,
};

export default CategoryTab;
